import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from '@proptr/api-client/react';
import { Card } from './components/ui/card.js';
import { ContactsPage } from './pages/ContactsPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { InquiriesPage } from './pages/InquiriesPage.js';
import { MyProptreePage } from './pages/MyProptreePage.js';
import { SettingsPage } from './pages/SettingsPage.js';

// Agent portal entry. The only auth path right now is the manager's
// "Login as Proptree" → opens this app at `/?_imp=…` (Phase 1). Direct
// agent self-login comes in a later phase.
export function App() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <RequireAgent>
            <DashboardPage />
          </RequireAgent>
        }
      />
      <Route
        path="/inquiries"
        element={
          <RequireAgent>
            <InquiriesPage />
          </RequireAgent>
        }
      />
      <Route
        path="/contacts"
        element={
          <RequireAgent>
            <ContactsPage />
          </RequireAgent>
        }
      />
      <Route
        path="/proptree"
        element={
          <RequireAgent>
            <MyProptreePage />
          </RequireAgent>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireAgent>
            <SettingsPage />
          </RequireAgent>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Auth + role gate. Renders the inner page only when /me confirms an
// agent session. Unauthenticated users see a "no session" notice (no
// self-login UI yet); managers/admins who somehow land here get a "wrong
// portal" message rather than being silently bounced.
function RequireAgent({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Centered>
        <Card className="p-6">
          <p className="font-mono text-[12px] text-muted-foreground">
            Loading…
          </p>
        </Card>
      </Centered>
    );
  }
  if (!user) {
    return (
      <Centered>
        <Card className="space-y-3 p-6">
          <h2 className="font-display text-[24px] font-medium leading-tight tracking-[-0.02em]">
            No session
          </h2>
          <p className="font-mono text-[12px] leading-relaxed text-muted-foreground">
            Open this portal from your manager dashboard via{' '}
            <strong className="text-foreground">
              Proptree → Login as Proptree
            </strong>{' '}
            on an agent row. Direct sign-in for agents is coming in a
            later phase.
          </p>
        </Card>
      </Centered>
    );
  }
  if (user.role !== 'agent') {
    return (
      <Centered>
        <Card className="space-y-3 p-6">
          <h2 className="font-display text-[24px] font-medium leading-tight tracking-[-0.02em]">
            Wrong portal
          </h2>
          <p className="font-mono text-[12px] leading-relaxed text-muted-foreground">
            This portal is for agents. You're signed in as{' '}
            <code className="text-foreground">{user.role}</code>.
          </p>
        </Card>
      </Centered>
    );
  }
  // Touch `location` so the eslint dep-tracker doesn't strip it — kept
  // in scope so future redirects can preserve `from` like the manager
  // portal does.
  void location;
  return <>{children}</>;
}

// Standalone container for the unauth / wrong-role notices — they live
// outside the AppShell because the nav doesn't apply when there's no
// authenticated agent yet.
function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="grid min-h-screen place-items-center bg-background px-6 py-8">
      <div className="w-full max-w-md">{children}</div>
    </div>
  );
}
